import { useEffect, useState, type ComponentType } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Check,
  CircleCheck,
  CircleX,
  CloudOff,
  Copy,
  Hourglass,
  KeyRound,
  Lock,
  RotateCcw,
  TriangleAlert,
  WifiOff,
} from "lucide-react";
import { toolboxPatFixPath } from "@/components/ado/ado-pat-callout";
import { useAdoPatStatus } from "@/hooks/queries/use-me";
import { useCopyFeedback } from "@/hooks/use-copy-feedback";
import {
  accessRequestMessage,
  adoReadCopy,
  adoReadWhat,
  automaticRetryHappened,
  browserOnline,
  classifyAdoReadError,
  type AdoReadCause,
  type AdoReadFailure,
  type AdoReadSubject,
} from "@/lib/grooming/ado-read-error";
import { adminDetailsText } from "@/lib/grooming/copy-details";
import { formatLocalClockParts } from "@/lib/grooming/local-time";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";

/**
 * The B6 error card (epic PI-SPEC-SW-E290, feature PI-SPEC-SW-1480,
 * UX-PM-37; Claude Design "PM Packages B E F Mockups" frame B6).
 *
 * Replaces the single red sentence a failed Azure DevOps read used to show
 * on the Backlog, Session build and the reading pane: the cause in plain
 * words (`lib/grooming/ado-read-error.ts`), what was already tried, a short
 * checklist of what works and what didn't, one thing to do (Try again,
 * Renew the token or Copy a message), "Copy details for the Admin" and the
 * last-tried time. Try again is a TanStack Query refetch of the failed read,
 * never a direct request. Offline, the card reloads by itself on the
 * browser's `online` event.
 *
 * `compact` fits it inside the Session build column and the reading pane;
 * otherwise it is 720 px wide, centred under the page header.
 */

/** The parts of a TanStack Query result the card reads. */
export interface AdoReadQuery {
  error: unknown;
  /** TanStack's failure count of the last fetch: 2 after a failure, the automatic retry and a second failure. */
  failureCount: number;
  /** When the read last failed (epoch ms); 0 when it has not failed (offline before the first read). */
  errorUpdatedAt: number;
  isFetching: boolean;
  refetch: (options?: { cancelRefetch?: boolean }) => Promise<unknown>;
}

export interface AdoReadErrorCardProps {
  query: AdoReadQuery;
  subject: AdoReadSubject;
  /** The ADO project, for the no-access sentence and Copy details. */
  project?: string | undefined;
  /** The item read, for Copy details (the reading pane). */
  itemId?: string | undefined;
  compact?: boolean | undefined;
  testId?: string | undefined;
  className?: string | undefined;
}

/** The browser's online state, following its `online` and `offline` events. */
function useBrowserOnline(): boolean {
  const [online, setOnline] = useState(browserOnline);
  useEffect(() => {
    const update = () => {
      setOnline(browserOnline());
    };
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}

/** "Last tried 9:42 AM" today, "Last tried yesterday at 9:42 AM" before. Null when it never failed. */
export function lastTriedWords(at: number, now: number = Date.now()): string | null {
  if (!Number.isFinite(at) || at <= 0) return null;
  const parts = formatLocalClockParts(new Date(at).toISOString(), { now });
  if (!parts) return null;
  return parts.day ? `Last tried ${parts.day} at ${parts.time}` : `Last tried ${parts.time}`;
}

const CAUSE_ICONS: Readonly<Record<AdoReadCause, ComponentType<{ className?: string; "aria-hidden"?: boolean }>>> = {
  "ado-no-reply": CloudOff,
  offline: WifiOff,
  token: KeyRound,
  "no-access": Lock,
  "rate-limited": Hourglass,
  "not-set-up": TriangleAlert,
  "not-found": TriangleAlert,
  other: TriangleAlert,
};

const PRIMARY_BUTTON =
  "inline-flex h-[30px] items-center gap-1.5 rounded-lg bg-primary px-3 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 [&_svg]:h-3.5 [&_svg]:w-3.5";
const OUTLINE_BUTTON =
  "inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-[#27272A] bg-[#1A1A1E] px-3 text-[13px] font-medium text-foreground hover:bg-[#222226] disabled:opacity-50 [&_svg]:h-3.5 [&_svg]:w-3.5";
const GHOST_BUTTON =
  "inline-flex h-[30px] items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium text-muted-foreground hover:bg-[#141417] hover:text-foreground disabled:opacity-50 [&_svg]:h-3.5 [&_svg]:w-3.5";

/** A button that copies text and says "Copied" for two seconds. */
function CopyAction({ text, label, className, testId }: { text: string; label: string; className: string; testId: string }) {
  const { copied, copy } = useCopyFeedback();
  return (
    <button
      type="button"
      className={className}
      data-testid={testId}
      onClick={() => {
        copy(text);
      }}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      {copied ? "Copied" : label}
    </button>
  );
}

/** The failed Azure DevOps read card; one plain sentence for "not set up" and "not found". */
export function AdoReadErrorCard(props: AdoReadErrorCardProps) {
  const online = useBrowserOnline();
  const failure = classifyAdoReadError(props.query.error, online);
  const { refetch } = props.query;
  const offline = failure.cause === "offline";

  // Offline: reload by itself when the browser is back. Joins a refetch TanStack may already have started.
  useEffect(() => {
    if (!offline) return undefined;
    const reload = () => {
      void refetch({ cancelRefetch: false });
    };
    window.addEventListener("online", reload);
    return () => {
      window.removeEventListener("online", reload);
    };
  }, [offline, refetch]);

  if (failure.cause === "ado-no-reply") return <CardWithOrganization {...props} failure={failure} />;
  return <CardView {...props} failure={failure} organization={null} />;
}

/** The no-reply card names the ADO organisation, which SpecTree knows from the PAT how-to. */
function CardWithOrganization(props: AdoReadErrorCardProps & { failure: AdoReadFailure }) {
  const howTo = useAdoPatStatus();
  return <CardView {...props} organization={howTo.data?.organization ?? null} />;
}

function CardView({
  query,
  subject,
  project,
  itemId,
  compact = false,
  testId,
  className,
  failure,
  organization,
}: AdoReadErrorCardProps & { failure: AdoReadFailure; organization: string | null }) {
  const location = useLocation();
  const bridge = useToolboxBridge();
  const email = useAuthStore((state) => state.user?.email ?? null);
  const retried = automaticRetryHappened(failure, query.failureCount);
  const copy = adoReadCopy(failure, { subject, retried, project, organization });

  if (copy.title === null) {
    return (
      <p className={cn("text-sm text-destructive", className)} role="alert" data-testid={testId}>
        {copy.body}
      </p>
    );
  }

  const Icon = CAUSE_ICONS[failure.cause];
  const lastTried = lastTriedWords(query.errorUpdatedAt);
  const tryAgain = (variant: "primary" | "ghost") => (
    <button
      type="button"
      className={variant === "primary" ? PRIMARY_BUTTON : GHOST_BUTTON}
      disabled={query.isFetching}
      data-testid="ado-read-try-again"
      onClick={() => {
        void query.refetch();
      }}
    >
      <RotateCcw aria-hidden="true" />
      {query.isFetching ? "Trying again…" : "Try again"}
    </button>
  );
  const details = adminDetailsText({
    what: adoReadWhat(subject, failure),
    project,
    itemId,
    errorCode: failure.code,
    rawMessage: failure.message,
    at: query.errorUpdatedAt > 0 ? query.errorUpdatedAt : Date.now(),
    appVersion: bridge?.app.version,
    contracts: bridge?.app.contracts,
  });

  return (
    <div className={cn(!compact && "flex justify-center", className)}>
      <div
        role="alert"
        data-testid={testId}
        data-cause={failure.cause}
        className={cn(
          "flex w-full rounded-[10px] border border-[rgba(225,24,55,0.35)] bg-[#150C0E]",
          compact ? "gap-3 px-4 py-3.5" : "max-w-[720px] gap-4 px-[22px] py-5"
        )}
      >
        <Icon className={cn("mt-0.5 flex-none text-[#FF8A9B]", compact ? "h-5 w-5" : "h-[26px] w-[26px]")} aria-hidden={true} />
        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <h2 className={cn("font-display font-semibold leading-snug", compact ? "text-sm" : "text-base")}>{copy.title}</h2>
          <p className={cn("leading-[1.55] text-[#D4D4D8]", compact ? "text-[13px]" : "text-sm")} data-testid="ado-read-body">
            {copy.body}
          </p>
          {copy.checklist.length > 0 && (
            <ul
              className="flex flex-col gap-1.5 rounded-lg border border-[#1F1F23] bg-[#0B0B0D] px-3 py-2.5 text-[13px]"
              aria-label="What was checked"
              data-testid="ado-read-checklist"
            >
              {copy.checklist.map((check) => (
                <li key={check.text} className="flex items-center gap-2" data-ok={check.ok ? "true" : "false"}>
                  {check.ok ? (
                    <CircleCheck className="h-3.5 w-3.5 flex-none text-[#4ADE80]" aria-hidden="true" />
                  ) : (
                    <CircleX className="h-3.5 w-3.5 flex-none text-[#FF8A9B]" aria-hidden="true" />
                  )}
                  <span>
                    <span className="sr-only">{check.ok ? "Works: " : "Failed: "}</span>
                    {check.text}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {copy.primary === "try-again" && tryAgain("primary")}
            {copy.primary === "renew-token" && (
              <Link
                to={toolboxPatFixPath(location.pathname, location.search)}
                className={PRIMARY_BUTTON}
                data-testid="ado-read-renew-token"
              >
                <KeyRound aria-hidden="true" />
                Renew the token
              </Link>
            )}
            {copy.primary === "copy-message" && (
              <CopyAction
                text={accessRequestMessage(project, email)}
                label="Copy a message"
                className={OUTLINE_BUTTON}
                testId="ado-read-copy-message"
              />
            )}
            {copy.secondaryTryAgain && tryAgain("ghost")}
            {copy.copyDetails && (
              <CopyAction
                text={details}
                label="Copy details for the Admin"
                className={GHOST_BUTTON}
                testId="ado-read-copy-details"
              />
            )}
            <span className="flex-1" />
            {lastTried && (
              <span className={cn("text-xs", TOOLBOX_TEXT.faint)} data-testid="ado-read-last-tried">
                {lastTried}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
